import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MapPinned, Plus, Upload } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ADMIN_PROSPECTS_PAGE_SIZE,
  ADMIN_PROSPECT_LOOKUP_TYPES,
  ADMIN_PROSPECT_STATUSES,
  buildListAdminProspectsRequest,
  listAdminProspects,
} from '../../services/admin/prospectsService';
import {
  ADMIN_PROSPECT_TARGETING_CONTACT_STATES,
  ADMIN_PROSPECT_TARGETING_SOURCES,
  adminProspectTargetingHasActiveFilters,
  buildAdminProspectTargetingFilters,
  cloneAdminProspectTargeting,
  createAdminProspectRequestGate,
  describeAdminProspectTargetingChips,
  emptyAdminProspectTargetingForm,
  formatAdminProspectTargetingCount,
  targetingFilterErrorKey,
} from '../../services/admin/prospectTargetingFilters';
import {
  ADMIN_PROSPECT_SELECTION_ALL_MATCHING,
  countAdminProspectSelection,
  createAllMatchingAdminProspectSelection,
  createEmptyAdminProspectSelection,
  isAdminProspectPageFullySelected,
  isAdminProspectSelected,
  setAdminProspectPageSelected,
  toggleAdminProspectSelection,
} from '../../services/admin/prospectSelection';
import AdminCreateProspectDialog from './AdminCreateProspectDialog';
import AdminCreateProspectCampaignDialog from './AdminCreateProspectCampaignDialog';
import { formatImportFlash } from './AdminProspectImportPage';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminProspectListErrorKey,
  adminProspectStatusBadgeClass,
  adminProspectStatusLabel,
  classifyAdminCallableError,
  formatAdminDate,
  formatAdminProspectAddress,
  formatAdminProspectLocality,
  formatAdminProspectUnits,
} from './adminProspectDisplay';

function CardField({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-800">{children}</dd>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const label = adminProspectStatusLabel(status, t);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${adminProspectStatusBadgeClass(status)}`}
    >
      {label}
    </span>
  );
}

function hasQuickFilters(filters) {
  return Boolean(filters?.lookupType && filters?.query) || Boolean(filters?.status);
}

function isLightLoadStatus(value) {
  return value === 'ready' || value === 'paging' || value === 'empty' || value === 'lookup-empty' || value === 'targeting-empty';
}

function selectRowLabel(item, t) {
  const address = formatAdminProspectAddress(item) || item.id;
  return t('admin.prospection.select.row').replace('{address}', address);
}

const EMPTY_QUICK_FILTERS = { mode: 'quick', lookupType: null, query: null, status: null };

export default function AdminProspectsPage() {
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const requestGateRef = useRef(createAdminProspectRequestGate());
  const [listMode, setListMode] = useState('targeting');
  const [searchType, setSearchType] = useState('address');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [draftFilters, setDraftFilters] = useState(emptyAdminProspectTargetingForm);
  const [appliedTargeting, setAppliedTargeting] = useState({});
  const [filterErrors, setFilterErrors] = useState({});
  const [prospects, setProspects] = useState([]);
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenStack, setTokenStack] = useState([]);
  const [currentToken, setCurrentToken] = useState(null);
  const [activeFilters, setActiveFilters] = useState({ mode: 'targeting', targeting: {} });
  const [totalCount, setTotalCount] = useState(null);
  const [selection, setSelection] = useState(createEmptyAdminProspectSelection);
  const [createOpen, setCreateOpen] = useState(false);
  const [campaignCreateOpen, setCampaignCreateOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const busy = status === 'loading' || status === 'paging';
  const targetingMode = listMode === 'targeting';
  const pageIds = prospects.map((item) => item.id);
  const pageFullySelected = isAdminProspectPageFullySelected(selection, pageIds);
  const selectedCount = countAdminProspectSelection(selection, totalCount);
  const chips = targetingMode ? describeAdminProspectTargetingChips(appliedTargeting, t) : [];
  const showSelectAllMatching =
    targetingMode && Number.isInteger(totalCount) && totalCount > prospects.length && prospects.length > 0;

  const applyList = (data, filters, { paging = false, nextStack, pageToken } = {}) => {
    const list = Array.isArray(data?.prospects) ? data.prospects : [];
    setProspects(list);
    setActiveFilters(filters);
    setNextPageToken(data?.nextPageToken || null);
    setCurrentToken(pageToken || null);
    if (Array.isArray(nextStack)) setTokenStack(nextStack);
    else if (!paging) setTokenStack([]);
    const isTargeting = filters?.mode === 'targeting';
    if (isTargeting) {
      if (!paging && Number.isInteger(data?.totalCount) && data.totalCount >= 0) {
        setTotalCount(data.totalCount);
      }
    } else {
      setTotalCount(null);
    }
    const emptyKind = isTargeting
      ? adminProspectTargetingHasActiveFilters(filters.targeting)
        ? 'targeting-empty'
        : 'empty'
      : hasQuickFilters(filters)
        ? 'lookup-empty'
        : 'empty';
    setStatus(list.length === 0 ? emptyKind : 'ready');
  };

  const fail = (err) => {
    setProspects([]);
    setNextPageToken(null);
    setErrorKind(classifyAdminCallableError(err));
    setStatus('error');
  };

  const loadList = async (filters, pageToken, { paging = false, nextStack } = {}) => {
    const requestId = requestGateRef.current.next();
    const isTargeting = filters?.mode === 'targeting';
    if (!paging) setTotalCount(null);
    setStatus(paging ? 'paging' : 'loading');
    setErrorKind(null);
    try {
      const data = await listAdminProspects(
        isTargeting
          ? buildListAdminProspectsRequest({
              pageSize: ADMIN_PROSPECTS_PAGE_SIZE,
              pageToken,
              targeting: filters.targeting || {},
              includeTotalCount: !paging,
            })
          : buildListAdminProspectsRequest({
              pageSize: ADMIN_PROSPECTS_PAGE_SIZE,
              pageToken,
              lookupType: filters.lookupType,
              query: filters.query,
              status: filters.status,
            }),
      );
      if (!requestGateRef.current.isCurrent(requestId)) return;
      applyList(data, filters, { paging, nextStack, pageToken });
    } catch (err) {
      if (!requestGateRef.current.isCurrent(requestId)) return;
      fail(err);
    }
  };

  useEffect(() => {
    loadList({ mode: 'targeting', targeting: {} }, undefined);
  }, []);

  useEffect(() => {
    if (location.state?.importResult) {
      setSuccessMessage(formatImportFlash(location.state.importResult, t));
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (!location.state?.prospectDeleted) return;
    setSuccessMessage(t('admin.prospection.delete.success'));
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, t]);

  const clearSelection = () => setSelection(createEmptyAdminProspectSelection());

  const handleSearch = async (event) => {
    event.preventDefault();
    setTokenStack([]);
    setCurrentToken(null);
    setSuccessMessage('');
    clearSelection();
    const filters = {
      mode: 'quick',
      lookupType: searchInput.trim() ? searchType : null,
      query: searchInput.trim() || null,
      status: statusFilter || null,
    };
    await loadList(filters, undefined, { paging: isLightLoadStatus(status) });
  };

  const handleStatusChange = async (event) => {
    const nextStatus = event.target.value;
    setStatusFilter(nextStatus);
    setTokenStack([]);
    setCurrentToken(null);
    setSuccessMessage('');
    clearSelection();
    const filters = {
      mode: 'quick',
      lookupType: activeFilters.lookupType,
      query: activeFilters.query,
      status: nextStatus || null,
    };
    await loadList(filters, undefined, { paging: isLightLoadStatus(status) });
  };

  const handleQuickReset = () => {
    setSearchInput('');
    setSearchType('address');
    setStatusFilter('');
    setTokenStack([]);
    setCurrentToken(null);
    setSuccessMessage('');
    setErrorKind(null);
    clearSelection();
    loadList({ ...EMPTY_QUICK_FILTERS }, undefined);
  };

  const applyTargetingFilters = (targeting) => {
    const snapshot = cloneAdminProspectTargeting(targeting);
    setAppliedTargeting(snapshot);
    setFilterErrors({});
    setTokenStack([]);
    setCurrentToken(null);
    setSuccessMessage('');
    clearSelection();
    loadList({ mode: 'targeting', targeting: cloneAdminProspectTargeting(snapshot) }, undefined);
  };

  const handleApplyTargeting = (event) => {
    event.preventDefault();
    if (busy) return;
    const built = buildAdminProspectTargetingFilters(draftFilters);
    if (!built.ok) {
      setFilterErrors(built.errors);
      return;
    }
    applyTargetingFilters(built.targeting);
  };

  const handleResetTargeting = () => {
    if (busy && status === 'loading') return;
    setDraftFilters(emptyAdminProspectTargetingForm());
    applyTargetingFilters({});
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === listMode) return;
    setListMode(nextMode);
    setTokenStack([]);
    setCurrentToken(null);
    setSuccessMessage('');
    setErrorKind(null);
    setFilterErrors({});
    clearSelection();
    if (nextMode === 'targeting') {
      setDraftFilters(emptyAdminProspectTargetingForm());
      setAppliedTargeting({});
      loadList({ mode: 'targeting', targeting: {} }, undefined);
    } else {
      setSearchInput('');
      setSearchType('address');
      setStatusFilter('');
      setAppliedTargeting({});
      setTotalCount(null);
      loadList({ ...EMPTY_QUICK_FILTERS }, undefined);
    }
  };

  const canPage = !busy && (status === 'ready' || status === 'paging' || status === 'empty' || status === 'lookup-empty' || status === 'targeting-empty');

  const handleNext = () => {
    if (!canPage || !nextPageToken) return;
    const nextStack = [...tokenStack, currentToken];
    loadList(activeFilters, nextPageToken, { paging: true, nextStack });
  };

  const handlePrevious = () => {
    if (!canPage || tokenStack.length === 0) return;
    const nextStack = tokenStack.slice(0, -1);
    const previousToken = tokenStack[tokenStack.length - 1];
    loadList(activeFilters, previousToken || undefined, { paging: true, nextStack });
  };

  const reloadCurrentList = () => {
    setTokenStack([]);
    setCurrentToken(null);
    loadList(activeFilters, undefined);
  };

  const handleCreated = () => {
    setSuccessMessage(t('admin.prospection.create.success'));
    reloadCurrentList();
  };

  const handleToggleRow = (prospectId) => {
    setSelection((current) => toggleAdminProspectSelection(current, prospectId));
  };

  const handleTogglePage = () => {
    setSelection((current) => setAdminProspectPageSelected(current, pageIds, !pageFullySelected));
  };

  const handleSelectAllMatching = () => {
    setSelection(createAllMatchingAdminProspectSelection(appliedTargeting));
  };

  const errorMessage = t(adminProspectListErrorKey(errorKind));
  const showPagination =
    status === 'ready' || status === 'paging' || status === 'empty' || status === 'lookup-empty' || status === 'targeting-empty';
  const countLabel = targetingMode ? formatAdminProspectTargetingCount(totalCount, t) : '';
  const selectionLabel =
    selection.mode === ADMIN_PROSPECT_SELECTION_ALL_MATCHING
      ? t('admin.prospection.select.countAllMatching').replace('{count}', String(selectedCount))
      : t('admin.prospection.select.count').replace('{count}', String(selectedCount));

  const fieldClass = (hasError) =>
    `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
      hasError ? 'border-red-400' : 'border-slate-300'
    }`;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2">
          <MapPinned className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{t('admin.prospection.list.title')}</h1>
            <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.subtitle')}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Link
            to="/admin/prospection/import"
            className="inline-flex items-center justify-center gap-1 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {t('admin.prospection.import')}
          </Link>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('admin.prospection.new')}
          </button>
        </div>
      </div>

      {successMessage ? (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" role="status">
          {successMessage}
        </p>
      ) : null}

      <div role="tablist" aria-label={t('admin.prospection.list.title')} className="flex flex-wrap gap-2">
        <button
          type="button"
          role="tab"
          id="admin-prospection-mode-targeting"
          aria-selected={targetingMode}
          onClick={() => handleModeChange('targeting')}
          className={`rounded-lg px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            targetingMode
              ? 'bg-indigo-600 text-white'
              : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          {t('admin.prospection.mode.targeting')}
        </button>
        <button
          type="button"
          role="tab"
          id="admin-prospection-mode-quick"
          aria-selected={!targetingMode}
          onClick={() => handleModeChange('quick')}
          className={`rounded-lg px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            !targetingMode
              ? 'bg-indigo-600 text-white'
              : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          {t('admin.prospection.mode.quick')}
        </button>
      </div>

      {targetingMode ? (
        <form
          onSubmit={handleApplyTargeting}
          className="space-y-3 rounded-xl border border-slate-200 bg-white p-4"
        >
          <h2 className="text-sm font-semibold text-slate-900">{t('admin.prospection.targeting.title')}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.prospection.targeting.city')}</span>
              <input
                id="admin-prospection-targeting-city"
                type="text"
                value={draftFilters.city}
                onChange={(event) => setDraftFilters((current) => ({ ...current, city: event.target.value }))}
                autoComplete="off"
                className={fieldClass(false)}
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.prospection.targeting.postal')}</span>
              <input
                id="admin-prospection-targeting-postal"
                type="text"
                value={draftFilters.postalCode}
                onChange={(event) => setDraftFilters((current) => ({ ...current, postalCode: event.target.value }))}
                aria-invalid={Boolean(filterErrors.postalCode)}
                aria-describedby={filterErrors.postalCode ? 'admin-prospection-targeting-postal-error' : undefined}
                autoComplete="off"
                className={fieldClass(Boolean(filterErrors.postalCode))}
              />
              {filterErrors.postalCode ? (
                <p id="admin-prospection-targeting-postal-error" className="mt-1 text-xs text-red-700">
                  {t(targetingFilterErrorKey(filterErrors.postalCode))}
                </p>
              ) : null}
            </label>
            <div className="text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.prospection.targeting.units')}</span>
              <div className="flex items-center gap-2">
                <label className="min-w-0 flex-1">
                  <span className="sr-only">{t('admin.prospection.targeting.minUnits')}</span>
                  <input
                    id="admin-prospection-targeting-min-units"
                    inputMode="numeric"
                    value={draftFilters.minUnits}
                    onChange={(event) => setDraftFilters((current) => ({ ...current, minUnits: event.target.value }))}
                    aria-invalid={Boolean(filterErrors.minUnits)}
                    aria-describedby={filterErrors.minUnits ? 'admin-prospection-targeting-units-error' : undefined}
                    className={fieldClass(Boolean(filterErrors.minUnits))}
                  />
                </label>
                <span className="shrink-0 text-slate-500">{t('admin.prospection.targeting.units.to')}</span>
                <label className="min-w-0 flex-1">
                  <span className="sr-only">{t('admin.prospection.targeting.maxUnits')}</span>
                  <input
                    id="admin-prospection-targeting-max-units"
                    inputMode="numeric"
                    value={draftFilters.maxUnits}
                    onChange={(event) => setDraftFilters((current) => ({ ...current, maxUnits: event.target.value }))}
                    aria-invalid={Boolean(filterErrors.maxUnits)}
                    aria-describedby={filterErrors.maxUnits ? 'admin-prospection-targeting-units-error' : undefined}
                    className={fieldClass(Boolean(filterErrors.maxUnits))}
                  />
                </label>
              </div>
              {filterErrors.minUnits || filterErrors.maxUnits ? (
                <p id="admin-prospection-targeting-units-error" className="mt-1 text-xs text-red-700">
                  {t(targetingFilterErrorKey(filterErrors.minUnits || filterErrors.maxUnits))}
                </p>
              ) : null}
            </div>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.prospection.targeting.status')}</span>
              <select
                id="admin-prospection-targeting-status"
                value={draftFilters.status}
                onChange={(event) => setDraftFilters((current) => ({ ...current, status: event.target.value }))}
                className={fieldClass(false)}
              >
                <option value="">{t('admin.prospection.targeting.status.all')}</option>
                {ADMIN_PROSPECT_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {t(`admin.prospection.status.${value}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.prospection.targeting.source')}</span>
              <select
                id="admin-prospection-targeting-source"
                value={draftFilters.source}
                onChange={(event) => setDraftFilters((current) => ({ ...current, source: event.target.value }))}
                className={fieldClass(false)}
              >
                <option value="">{t('admin.prospection.targeting.source.all')}</option>
                {ADMIN_PROSPECT_TARGETING_SOURCES.map((value) => (
                  <option key={value} value={value}>
                    {t(`admin.prospection.targeting.source.${value}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.prospection.targeting.contact')}</span>
              <select
                id="admin-prospection-targeting-contact"
                value={draftFilters.contactState}
                onChange={(event) => setDraftFilters((current) => ({ ...current, contactState: event.target.value }))}
                className={fieldClass(false)}
              >
                <option value="">{t('admin.prospection.targeting.contact.all')}</option>
                {ADMIN_PROSPECT_TARGETING_CONTACT_STATES.map((value) => (
                  <option key={value} value={value}>
                    {t(`admin.prospection.targeting.contact.${value}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.targeting.apply')}
            </button>
            <button
              type="button"
              onClick={handleResetTargeting}
              disabled={busy && status === 'loading'}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.targeting.reset')}
            </button>
          </div>
          {chips.length > 0 ? (
            <ul className="flex flex-wrap gap-2" aria-label={t('admin.prospection.targeting.title')}>
              {chips.map((chip) => (
                <li
                  key={chip.id}
                  className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-800 ring-1 ring-inset ring-indigo-200"
                >
                  {chip.label}
                </li>
              ))}
            </ul>
          ) : null}
        </form>
      ) : (
        <form onSubmit={handleSearch} className="flex flex-col gap-2 lg:flex-row lg:items-end">
          <label className="text-sm lg:w-48">
            <span className="mb-1 block text-slate-600">{t('admin.prospection.search.type')}</span>
            <select
              id="admin-prospection-search-type"
              value={searchType}
              onChange={(event) => setSearchType(event.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {ADMIN_PROSPECT_LOOKUP_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`admin.prospection.search.type.${type}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="min-w-0 flex-1 text-sm">
            <span className="mb-1 block text-slate-600">{t('admin.prospection.search.label')}</span>
            <input
              id="admin-prospection-search"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t(`admin.prospection.search.placeholder.${searchType}`)}
              autoComplete="off"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </label>
          <label className="text-sm lg:w-56">
            <span className="mb-1 block text-slate-600">{t('admin.prospection.status.filter')}</span>
            <select
              id="admin-prospection-status-filter"
              value={statusFilter}
              onChange={handleStatusChange}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('admin.prospection.status.all')}</option>
              {ADMIN_PROSPECT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`admin.prospection.status.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.search.submit')}
            </button>
            <button
              type="button"
              onClick={handleQuickReset}
              disabled={busy && status === 'loading'}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.reset')}
            </button>
          </div>
        </form>
      )}

      {countLabel ? (
        <p className="text-base font-semibold text-slate-900" aria-live="polite" role="status">
          {countLabel}
        </p>
      ) : null}

      {selectedCount > 0 ? (
        <div
          className="flex flex-col gap-2 rounded-xl border border-indigo-200 bg-indigo-50 p-3 sm:flex-row sm:items-center sm:justify-between"
          role="status"
        >
          <p className="text-sm font-medium text-indigo-950">{selectionLabel}</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              id="admin-prospection-create-campaign"
              onClick={() => setCampaignCreateOpen(true)}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {t('admin.prospection.campaign.create.action')}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-800 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {t('admin.prospection.select.clear')}
            </button>
          </div>
        </div>
      ) : null}

      {prospects.length > 0 && (status === 'ready' || status === 'paging') ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleTogglePage}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {pageFullySelected ? t('admin.prospection.select.pageOff') : t('admin.prospection.select.page')}
          </button>
          {showSelectAllMatching ? (
            <button
              type="button"
              id="admin-prospection-select-all-matching"
              onClick={handleSelectAllMatching}
              className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {t('admin.prospection.select.allMatching').replace('{count}', String(totalCount))}
            </button>
          ) : null}
        </div>
      ) : null}

      {status === 'loading' ? (
        <div className="space-y-2" aria-busy="true" aria-live="polite">
          <p className="text-sm text-slate-500">{t('admin.prospection.loading')}</p>
          <div className="hidden h-40 animate-pulse rounded-xl bg-slate-100 md:block" />
          <div className="h-28 animate-pulse rounded-xl bg-slate-100 md:hidden" />
        </div>
      ) : null}
      {status === 'paging' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.prospection.paging')}
        </p>
      ) : null}
      {status === 'empty' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          <p>{t('admin.prospection.empty')}</p>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="mt-3 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('admin.prospection.empty.cta')}
          </button>
        </div>
      ) : null}
      {status === 'lookup-empty' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.prospection.lookupEmpty')}
        </p>
      ) : null}
      {status === 'targeting-empty' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          <p>{t('admin.prospection.targeting.empty')}</p>
          <button
            type="button"
            onClick={handleResetTargeting}
            className="mt-3 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('admin.prospection.targeting.resetFilters')}
          </button>
        </div>
      ) : null}
      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={() => loadList(activeFilters, currentToken || undefined)}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.prospection.retry')}
          </button>
        </div>
      ) : null}

      {prospects.length > 0 && (status === 'ready' || status === 'paging') ? (
        <>
          <div className="hidden rounded-xl border border-slate-200 bg-white md:block">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">{t('admin.prospection.list.title')}</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="w-10 px-3 py-2 font-semibold">
                    <span className="sr-only">{t('admin.prospection.select.col')}</span>
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.prospection.col.address')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.prospection.col.city')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.prospection.col.units')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.prospection.col.owner')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.prospection.col.status')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.prospection.col.nextFollowUp')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.prospection.col.created')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.prospection.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {prospects.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isAdminProspectSelected(selection, item.id)}
                        onChange={() => handleToggleRow(item.id)}
                        aria-label={selectRowLabel(item, t)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      <Link
                        to={`/admin/prospection/${encodeURIComponent(item.id)}`}
                        className="text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {formatAdminProspectAddress(item) || ADMIN_DATE_PLACEHOLDER}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      {item.city || ADMIN_DATE_PLACEHOLDER}
                      {item.postalCode ? (
                        <span className="mt-0.5 block text-xs text-slate-500">{item.postalCode}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">{formatAdminProspectUnits(item.numberOfUnits)}</td>
                    <td className="px-3 py-2">{item.ownerName || ADMIN_DATE_PLACEHOLDER}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={item.status} t={t} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatAdminDate(item.nextFollowUpAt, language)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatAdminDate(item.createdAt, language)}</td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/admin/prospection/${encodeURIComponent(item.id)}`}
                        className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {t('admin.prospection.open360')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {prospects.map((item) => (
              <article key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isAdminProspectSelected(selection, item.id)}
                    onChange={() => handleToggleRow(item.id)}
                    aria-label={selectRowLabel(item, t)}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900">
                      <Link
                        to={`/admin/prospection/${encodeURIComponent(item.id)}`}
                        className="text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {formatAdminProspectAddress(item) || ADMIN_DATE_PLACEHOLDER}
                      </Link>
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {formatAdminProspectLocality(item) || ADMIN_DATE_PLACEHOLDER}
                    </p>
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-3">
                  <CardField label={t('admin.prospection.col.units')}>
                    {formatAdminProspectUnits(item.numberOfUnits)}
                  </CardField>
                  {item.ownerName ? (
                    <CardField label={t('admin.prospection.col.owner')}>{item.ownerName}</CardField>
                  ) : null}
                  <CardField label={t('admin.prospection.col.status')}>
                    <StatusBadge status={item.status} t={t} />
                  </CardField>
                  <CardField label={t('admin.prospection.col.nextFollowUp')}>
                    {formatAdminDate(item.nextFollowUpAt, language)}
                  </CardField>
                </dl>
                <div className="mt-3">
                  <Link
                    to={`/admin/prospection/${encodeURIComponent(item.id)}`}
                    className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {t('admin.prospection.open360')}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}

      {showPagination ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={busy || tokenStack.length === 0}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.prospection.previous')}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={busy || !nextPageToken}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.prospection.next')}
          </button>
        </div>
      ) : null}

      <AdminCreateProspectDialog
        open={createOpen}
        t={t}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
      <AdminCreateProspectCampaignDialog
        open={campaignCreateOpen}
        t={t}
        selection={selection}
        selectedCount={selectedCount}
        onClose={() => setCampaignCreateOpen(false)}
      />
    </div>
  );
}
